import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    ICreateRegistryRequest,
    IDeleteRegistryResponse,
    IRegistry,
    IRegistryListParams,
    IRegistryListResponse,
    IResolveEntriesRequest,
    IResolveEntriesResponse,
    IUpdateRegistryRequest,
} from '../../models/registry.interface';
import { environment } from '../../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class RegistryService {
    static readonly endpoints = {
        list: `${environment.apiUrl}/registries`,
        detail: (id: string) => `${environment.apiUrl}/registries/${encodeURIComponent(id)}`,
        resolveEntries: `${environment.apiUrl}/registries/resolve-entries`,
    };

    private http = inject(HttpClient);

    list(params?: IRegistryListParams): Observable<IRegistryListResponse> {
        let httpParams = new HttpParams();
        if (params?.identity_type) {
            httpParams = httpParams.set('identity_type', params.identity_type);
        }
        if (params?.search) {
            httpParams = httpParams.set('search', params.search);
        }
        if (params?.page != null) {
            httpParams = httpParams.set('page', params.page.toString());
        }
        if (params?.page_size != null) {
            httpParams = httpParams.set('page_size', params.page_size.toString());
        }
        return this.http.get<IRegistryListResponse>(RegistryService.endpoints.list, { params: httpParams });
    }

    get(registryId: string): Observable<IRegistry> {
        return this.http.get<IRegistry>(RegistryService.endpoints.detail(registryId));
    }

    create(body: ICreateRegistryRequest): Observable<IRegistry> {
        return this.http.post<IRegistry>(RegistryService.endpoints.list, body);
    }

    update(registryId: string, body: IUpdateRegistryRequest): Observable<IRegistry> {
        return this.http.put<IRegistry>(RegistryService.endpoints.detail(registryId), body);
    }

    delete(registryId: string, forceDelete = false): Observable<IDeleteRegistryResponse> {
        let httpParams = new HttpParams();
        if (forceDelete) {
            httpParams = httpParams.set('confirm_rule_deletion', 'true');
        }
        return this.http.delete<IDeleteRegistryResponse>(
            RegistryService.endpoints.detail(registryId),
            { params: httpParams },
        );
    }

    resolveEntries(body: IResolveEntriesRequest): Observable<IResolveEntriesResponse> {
        return this.http.post<IResolveEntriesResponse>(RegistryService.endpoints.resolveEntries, body);
    }
}
